import LanguageControl from '../i18n/LanguageControl';
import { text, languageUrl } from '../i18n/lab';
import { initializeLanguage } from '../i18n/locale';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getLabProject } from '../data/labProjects';
import { trackPageView, trackButtonClick } from '../utils/analytics';
import '../styles/lab.css';

const LabProject = () => {
  const { projectId } = useParams();
  const project = getLabProject(projectId);
  const [activeId, setActiveId] = useState(project ? project.variants[0].id : null);

  useEffect(() => {
    initializeLanguage(`${project?.title || 'Lab'} · PeaceTech`, project?.summary || 'PeaceTech Lab');
    trackPageView(`Lab — ${projectId}`);
  }, [projectId, project]);

  useEffect(() => {
    const icons = {
      'temple-of-heaven': '/lab/temple-of-heaven/favicon.svg',
      'mirror-hand-tracking': '/lab/mirror-hand-tracking/favicon.svg',
      'china-metro-spacetime': '/lab/china-metro-spacetime/favicon.svg',
      'yingxian-pagoda': '/lab/yingxian-pagoda/pagoda-icon.png',
      'foguang-temple': '/lab/foguang-temple/foguang-icon.png',
      'beijing-city-evolution': '/lab/beijing-city-evolution/favicon.svg',
      'pipa-rhythm': '/lab/pipa-rhythm/favicon.svg',
      'orbit-84': '/lab/orbit-84/favicon.svg',
    };
    const href = icons[projectId];
    if (!href) return;
    const icon = document.querySelector('link[rel="icon"]');
    if (!icon) return;
    const previous = ['href', 'type', 'sizes'].map((name) => [name, icon.getAttribute(name)]);
    icon.setAttribute('href', href);
    icon.setAttribute('type', href.endsWith('.svg') ? 'image/svg+xml' : 'image/png');
    icon.setAttribute('sizes', href.endsWith('.svg') ? 'any' : '32x32');
    return () => previous.forEach(([name, value]) => {
      if (value === null) icon.removeAttribute(name);
      else icon.setAttribute(name, value);
    });
  }, [projectId]);

  if (!project) {
    return (
      <div className="lab-container">
        <LanguageControl />
        <header className="lab-header">
          <h1>{text('未找到项目', 'Not found')}</h1>
          <p>{text('没有找到这个实验。', 'This experiment could not be found.')}</p>
        </header>
        <Link to={languageUrl("/lab")} className="lab-back">
          {text('← 返回 Lab', '← Back to Lab')}
        </Link>
      </div>
    );
  }

  const active = project.variants.find((v) => v.id === activeId) || project.variants[0];

  return (
    <div className="lab-container lab-project">
      <LanguageControl />
      <Link to={languageUrl("/lab")} className="lab-back">
        {text('← 返回 Lab', '← Back to Lab')}
      </Link>

      <header className="lab-header lab-project-header">
        <div className="lab-project-titles">
          <h1>{project.title}</h1>
          <span className="lab-project-subtitle">{project.subtitle}</span>
        </div>
        <p>{project.summary}</p>
      </header>

      <div className="lab-tabs" role="tablist">
        {project.variants.map((variant) => (
          <button
            key={variant.id}
            role="tab"
            aria-selected={variant.id === active.id}
            className={`lab-tab ${variant.id === active.id ? 'active' : ''}`}
            style={variant.id === active.id ? { '--accent': variant.accent } : undefined}
            onClick={() => {
              setActiveId(variant.id);
              trackButtonClick('Lab Variant', `${project.id}/${variant.id}`);
            }}
          >
            <span className="lab-tab-name">{variant.name}</span>
            <span className="lab-tab-desc">{variant.desc}</span>
          </button>
        ))}
      </div>

      <div className="lab-stage" style={{ '--accent': active.accent }}>
        <div className="lab-stage-bar">
          <span className="lab-stage-label">{active.name}</span>
          <a
            href={languageUrl(active.src)}
            target="_blank"
            rel="noopener noreferrer"
            className="lab-open-btn"
            onClick={() => trackButtonClick('Lab Open Fullscreen', `${project.id}/${active.id}`)}
          >
            {text('↗ 全屏打开', '↗ Open full screen')}
          </a>
        </div>
        <iframe
          key={active.id}
          className="lab-stage-frame"
          allow={project.iframeAllow}
          src={languageUrl(active.src)}
          title={`${project.title} — ${active.name}`}
        />
      </div>
    </div>
  );
};

export default LabProject;
